import {type CSSProperties, useMemo, useState} from "react";
import {type GetMatchHistoryQuery, useGetMatchHistoryQuery} from "../../generated/graphql";
import {
    Badge,
    Box,
    Card,
    Container,
    Group,
    type MantineTheme,
    Pagination,
    Select,
    Stack,
    Table,
    Text,
    Title,
    useMantineColorScheme,
    useMantineTheme
} from "@mantine/core";
import {DatePickerInput} from "@mantine/dates";
import {useNavigate} from "react-router-dom";
import dayjs from "dayjs";

/**
 * MatchHistory
 *
 * A global list of all recorded matches with client-side filtering.
 * - Uses mock data for now; designed to be trivially swapped for a real API.
 * - Filters: by team, franchise, and date range.
 * - Clicking a row navigates to the match details page (/matches/:matchId).
 */
type MatchHistoryItem = NonNullable<NonNullable<GetMatchHistoryQuery['listGameSeries']>['items']>[number];

export default function MatchHistory() {
    const navigate = useNavigate();
    const {colorScheme} = useMantineColorScheme();
    const theme = useMantineTheme();
    const isDark = colorScheme === "dark";

    // Call Core GameSeries match history query (data not yet wired to UI per request)
    const {data, loading, error} = useGetMatchHistoryQuery();

    const items = useMemo(() => (data?.listGameSeries?.items?.filter(Boolean) ?? []) as MatchHistoryItem[], [data]);

    // Mock data; replace with API hook in future (e.g., useMatchesQuery)
    //const data = useMemo(() => getMockMatches(), []);

    // Filter & view state
    const [teamFilter, setTeamFilter] = useState<string | null>(null);
    const [franchiseFilter, setFranchiseFilter] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string | null>(null);
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    // Build options from data (stable via useMemo)
    const teamOptions = useMemo(() => [
        {value: "", label: 'All teams'},
    ], []);

    const franchiseOptions = useMemo(() => [
        {value: "", label: 'All franchises'},
    ], []);

    const statusOptions = [
        {value: 'pending', label: 'Pending'},
        {value: 'completed', label: 'Completed'},
        {value: 'canceled', label: 'Canceled'},
    ];

    const filtered = useMemo(() => {
        return items.filter(m => {
            const teamOk = teamFilter ? (m?.homeTeam?.id === teamFilter || m?.awayTeam?.id === teamFilter) : true;
            const franchiseOk = franchiseFilter ? (m?.homeTeam?.franchiseId === franchiseFilter || m?.awayTeam?.franchiseId === franchiseFilter) : true;

            const [start, end] = dateRange;
            let dateOk = true;
            if (start || end) {
                const iso = m.scheduledStartTime ?? m.startTime ?? m.endTime;
                if (iso) {
                    const d = new Date(iso as unknown as string);
                    if (start && d < stripTime(start)) dateOk = false;
                    if (end && d > endOfDay(end)) dateOk = false;
                }
            }

            const statusLower = (m.status ?? '').toLowerCase();
            const statusOk = statusFilter ? statusLower === statusFilter.toLowerCase() : true;

            return teamOk && franchiseOk && dateOk && statusOk;
        });
    }, [items, teamFilter, franchiseFilter, dateRange, statusFilter]);

    // Pagination derived values
    const total = filtered?.length ?? 0;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    const pageSafe = Math.min(page, totalPages);
    const startIdx = (pageSafe - 1) * pageSize;
    const endIdx = Math.min(startIdx + pageSize, total);
    const pageItems = filtered?.slice(startIdx, endIdx);

    return (
        <Container fluid p="md">
            <Stack gap="md">
                <Group justify="space-between" align="center">
                    <Title order={3} className="dark:text-gray-300">Match History</Title>
                </Group>

                {/* Filters */}
                <Card withBorder radius="md" p="md">
                    <Stack gap="md">
                        <Group gap="md" wrap="wrap">
                            <Select
                                label="Team"
                                placeholder="All teams"
                                data={teamOptions}
                                value={teamFilter}
                                onChange={(v) => {
                                    setTeamFilter(v);
                                    setPage(1);
                                }}
                                clearable
                                searchable
                                nothingFoundMessage="No teams"
                            />
                            <Select
                                label="Franchise"
                                placeholder="All franchises"
                                data={franchiseOptions}
                                value={franchiseFilter}
                                onChange={(v) => {
                                    setFranchiseFilter(v);
                                    setPage(1);
                                }}
                                clearable
                                searchable
                                nothingFoundMessage="No franchises"
                            />
                            <Select
                                label="Status"
                                placeholder="All statuses"
                                data={statusOptions}
                                value={statusFilter}
                                onChange={(v) => {
                                    setStatusFilter(v);
                                    setPage(1);
                                }}
                                clearable
                            />
                            <DatePickerInput
                                type="range"
                                label="Date range"
                                placeholder="Pick dates"
                                value={dateRange}
                                onChange={(v) => {
                                    setDateRange(v);
                                    setPage(1);
                                }}
                                dropdownType="modal"
                                maxLevel="year"
                            />
                        </Group>
                        <Group justify="space-between" wrap="wrap">
                            <Text size="sm" c={isDark ? undefined : "dimmed"}>
                                Showing {total === 0 ? 0 : startIdx + 1}-{endIdx} of {data?.listGameSeries?.totalCount} matches
                            </Text>
                            <Group gap="xs">
                                <Text size="sm" c={isDark ? undefined : 'dimmed'}>Rows per page</Text>
                                <Select
                                    aria-label="Rows per page"
                                    data={[{value: '10', label: '10'}, {value: '20', label: '20'}, {
                                        value: '50',
                                        label: '50'
                                    }]}
                                    value={String(pageSize)}
                                    onChange={(v) => {
                                        const n = Number(v ?? 10);
                                        setPageSize(n);
                                        setPage(1);
                                    }}
                                    allowDeselect={false}
                                    w={100}
                                />
                            </Group>
                        </Group>
                    </Stack>
                </Card>

                {/* Results table */}
                <Card withBorder radius="md" p="md">
                    <Table striped withTableBorder highlightOnHover horizontalSpacing="md" verticalSpacing="xs"
                           style={{width: '100%'}}>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th style={{width: 200}}>Date</Table.Th>
                                <Table.Th style={{textAlign: 'center'}}>Match</Table.Th>
                                <Table.Th>Tournament</Table.Th>
                                <Table.Th style={{textAlign: 'center'}}>Status</Table.Th>
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                            {loading && (
                                <Table.Tr>
                                    <Table.Td colSpan={4}>
                                        <Text size="sm">Loading...</Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                            {!loading && error && (
                                <Table.Tr>
                                    <Table.Td colSpan={4}>
                                        <Text size="sm" c="red">Error: {error.message}</Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                            {!loading && !error && (pageItems?.length ?? 0) === 0 && (
                                <Table.Tr>
                                    <Table.Td colSpan={4}>
                                        <Text size="sm" c={isDark ? undefined : 'dimmed'}>No matches found</Text>
                                    </Table.Td>
                                </Table.Tr>
                            )}
                            {!loading && !error && pageItems?.map(m => {
                                return (
                                    <Table.Tr
                                        key={m.id}
                                        onClick={() => navigate(`/matches/${m.id}`)}
                                        style={{cursor: 'pointer'}}
                                    >
                                        <Table.Td
                                            style={{whiteSpace: 'nowrap'}}>{formatDateTime(m.scheduledStartTime ?? m.startTime ?? m.endTime)}</Table.Td>
                                        <Table.Td style={{textAlign: 'center'}}>
                                            <MatchCell match={m} isDark={isDark} theme={theme}/>
                                        </Table.Td>
                                        <Table.Td>N/A</Table.Td>
                                        <Table.Td style={{whiteSpace: 'nowrap', textAlign: 'center'}}>
                                            {renderStatusBadge(m, isDark, theme.primaryColor)}
                                        </Table.Td>
                                    </Table.Tr>
                                );
                            })}
                        </Table.Tbody>
                    </Table>

                    {/* Pagination controls */}
                    <Group justify="space-between" mt="md">
                        <Text size="sm" c={isDark ? undefined : 'dimmed'}>
                            Page {pageSafe} of {totalPages}
                        </Text>
                        <Pagination total={totalPages} value={pageSafe} onChange={setPage} withEdges size="sm"/>
                    </Group>
                </Card>
            </Stack>
        </Container>
    );
}

// Match cell renderer with optional W/L badges and in-cell gradient background
function MatchCell({match, isDark, theme}: { match: MatchHistoryItem; isDark: boolean; theme: MantineTheme }) {
    const {awayWins, homeWins} = parseSeriesScore(match);

    const draw = match.status === 'completed' && awayWins === homeWins;
    const homeIsWinner = match.status === 'completed' && awayWins < homeWins;
    const awayIsWinner = match.status === 'completed' && awayWins > homeWins;

    // Build a subtle in-cell gradient that fades in slightly "outside" the cell (using -10% start)
    // and fades out past the right edge (110%). The VS sits near the mid-point (~50%).
    const green = isDark ? 'rgba(64, 192, 87, 0.18)' : 'rgba(64, 192, 87, 0.12)'; // #40c057
    const red = isDark ? 'rgba(250, 82, 82, 0.18)' : 'rgba(250, 82, 82, 0.12)';   // #fa5252
    const transparent = 'rgba(0,0,0,0)';

    const showGradient = match.status === 'completed' && !draw;
    const gradientCss = showGradient
        ? (homeIsWinner
            // Left (home) wins: green on left transitioning to red on right
            ? `linear-gradient(90deg, ${transparent} -10%, ${green} 10%, ${green} 45%, ${red} 55%, ${red} 90%, ${transparent} 110%)`
            // Right (away) wins: red on left transitioning to green on right
            : `linear-gradient(90deg, ${transparent} -10%, ${red} 10%, ${red} 45%, ${green} 55%, ${green} 90%, ${transparent} 110%)`)
        : undefined;

    const badgeVariant = isDark ? 'filled' : 'light' as const;

    const WLBadge = ({kind, count}: { kind: 'W' | 'L' | 'D'; count?: number }) => (
        <Badge size="xs" variant={badgeVariant} color={kind === 'W' ? 'green' : kind === 'L' ? 'red' : 'gray'}
               radius="sm">
            {kind}{typeof count === 'number' ? `:${count}` : ''}
        </Badge>
    );

    return (
        <Box style={{position: 'relative', overflow: 'hidden'}}>
            {/* Gradient overlay contained to the cell */}
            {gradientCss && (
                <Box
                    aria-hidden
                    style={{
                        position: 'absolute',
                        inset: 0,
                        backgroundImage: gradientCss,
                        // Fade the gradient at cell edges so there's no hard start/stop
                        WebkitMaskImage: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0) 100%)',
                        maskImage: 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0) 100%)',
                        pointerEvents: 'none',
                        zIndex: 0,
                    }}
                />
            )}

            <Box style={{position: 'relative', zIndex: 1}}>
                {/* Three-column grid: left badge, centered teams, right badge */}
                <Box
                    style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto 1fr',
                        alignItems: 'center',
                        columnGap: 8,
                        paddingInline: 8, // keep badges away from extreme edges
                    } as CSSProperties}
                >
                    {/* Left badge column (aligned towards center) */}
                    <Box style={{justifySelf: 'end'}}>
                        {match.status === 'completed' ? (
                            <WLBadge kind={
                                homeWins === awayWins ? "D" :
                                    homeWins > awayWins ? "W" : "L"
                            } count={homeWins}/>
                        ) : <></>}
                    </Box>

                    {/* Center teams text */}
                    <Group gap={8} wrap="nowrap" align="center" justify="center">
                        <Text fw={600}>{match.homeTeam?.name}</Text>
                        <Text c={isDark ? undefined : 'dimmed'}>vs</Text>
                        <Text fw={600}>{match.awayTeam?.name}</Text>
                    </Group>

                    {/* Right badge column (aligned towards center) */}
                    <Box style={{justifySelf: 'start'}}>
                        {match.status === 'completed' ? (
                            <WLBadge kind={
                                homeWins === awayWins ? "D" :
                                    homeWins < awayWins ? "W" : "L"
                            } count={awayWins}/>
                        ) : <></>}
                    </Box>
                </Box>

                <Text size="xs" c={isDark ? undefined : 'dimmed'} ta="center">
                    {match.homeTeam?.franchise?.name ?? 'Unknown'} vs {match.awayTeam?.franchise?.name ?? 'Unknown'}
                </Text>
            </Box>
        </Box>
    );
}

// Date/time format can be customized here. Per requirements: time uses HH:mm (no seconds).
const DATE_TIME_FORMAT = "MMM D, YYYY HH:mm";

function formatDateTime(iso?: any) {
    if (!iso) return '-';
    try {
        return dayjs(iso).format(DATE_TIME_FORMAT);
    } catch {
        return String(iso ?? '-');
    }
}

function renderStatusBadge(m: MatchHistoryItem, isDark: boolean, primaryColor: string) {
    if (m?.status === 'pending') {
        return <Badge variant={isDark ? 'filled' : 'light'} color={primaryColor}>UPCOMING</Badge>;
    }
    if (m?.status === 'canceled') {
        return <Badge variant={isDark ? 'filled' : 'light'} color="gray">CANCELED</Badge>;
    }
    return (
        <Badge variant={isDark ? 'filled' : 'light'} color={primaryColor}>COMPLETED</Badge>
    );
}

function parseSeriesScore(match: MatchHistoryItem): { homeWins: number; awayWins: number } {
    if (!match) return {homeWins: 0, awayWins: 0};

    type MatchGame = NonNullable<NonNullable<MatchHistoryItem['games']>[number]>;
    const completeMatches: MatchGame[] = (match.games ?? []).filter(
        (g): g is MatchGame => !!g && g.status === "completed"
    );

    if (completeMatches.length > 0)
        return {
            homeWins: completeMatches.filter(g => (g.homeTeamScore ?? 0) > (g.awayTeamScore ?? 0)).length,
            awayWins: completeMatches.filter(g => (g.awayTeamScore ?? 0) > (g.homeTeamScore ?? 0)).length
        };

    return { homeWins: 0, awayWins: 0 };
}

function stripTime(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

function endOfDay(d: Date) {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
}
